import { useState } from "react";
import { FileDown, Loader2, Presentation } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { SECTIONS, useDeck } from "../../lib/deck-context";
import { buildSlides, savePdf, savePptx } from "../../lib/exporter";

const DEFAULT = ["overview", "property", "concept", "market", "revenue", "budget", "caprate", "valuation", "capital", "exit", "jv"];
const NAME = "Murrieta_Family_Commons_JV_Presentation";

export const ExportDialog = ({ open, onOpenChange }) => {
  const { go, idx, present, setPresent } = useDeck();
  const [sel, setSel] = useState(DEFAULT);
  const [busy, setBusy] = useState(null);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async (kind) => {
    const list = SECTIONS.map((s, i) => ({ ...s, index: i })).filter((s) => sel.includes(s.id));
    const back = { idx, present };
    onOpenChange(false);
    setBusy("Preparing presentation view…");
    setPresent(true);
    try {
      const slides = await buildSlides(list, go, setBusy);
      setBusy(`Building ${kind === "pdf" ? "PDF" : "PowerPoint"} · ${slides.length} slides…`);
      await (kind === "pdf" ? savePdf : savePptx)(slides, NAME);
      toast.success(`${kind === "pdf" ? "PDF" : "PowerPoint"} downloaded · ${slides.length} slides`);
    } catch (e) {
      toast.error("Export failed — please try again");
      console.error(e);
    } finally {
      setPresent(back.present);
      go(back.idx);
      setBusy(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="export-dialog" className="max-w-xl bg-paper border-line">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-ink">Export presentation</DialogTitle>
            <DialogDescription className="text-steel">Widescreen slides rendered exactly as presentation mode, using the numbers currently on screen.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-between items-center mt-2 font-mono text-[11px] tracking-[0.18em] text-steel">
            <span>SECTIONS · {sel.length} SELECTED</span>
            <span className="flex gap-3">
              <button data-testid="export-select-all" onClick={() => setSel(SECTIONS.map((s) => s.id))} className="hover:text-arq">ALL</button>
              <button data-testid="export-select-none" onClick={() => setSel([])} className="hover:text-arq">NONE</button>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 max-h-[46vh] overflow-y-auto">
            {SECTIONS.map((s) => (
              <label key={s.id} className="flex items-center gap-3 py-2 cursor-pointer text-sm text-char">
                <Checkbox data-testid={`export-check-${s.id}`} checked={sel.includes(s.id)} onCheckedChange={() => toggle(s.id)} className="data-[state=checked]:bg-arq data-[state=checked]:border-arq" />
                <span className="font-mono text-[11px] text-arq">{s.num}</span> {s.label}
              </label>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="export-pdf-btn" disabled={!sel.length} onClick={() => run("pdf")} className="flex items-center justify-center gap-2 rounded-full bg-arq py-3 text-sm text-white hover:bg-arq-dark transition-colors disabled:opacity-40">
              <FileDown size={16} /> Presentation PDF
            </button>
            <button data-testid="export-pptx-btn" disabled={!sel.length} onClick={() => run("pptx")} className="flex items-center justify-center gap-2 rounded-full border border-ink py-3 text-sm text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-40">
              <Presentation size={16} /> Create PowerPoint
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {busy && (
        <div data-testid="export-progress" className="fixed inset-0 z-[100] grid place-items-center bg-ink-deep/95 backdrop-blur-xl text-white">
          <div className="text-center">
            <Loader2 className="mx-auto animate-spin text-arq-mid" size={34} />
            <div className="mt-6 font-display text-3xl">Building your presentation</div>
            <div data-testid="export-progress-label" className="mt-3 font-mono text-xs tracking-[0.15em] text-white/60">{busy}</div>
          </div>
        </div>
      )}
    </>
  );
};
